import React, { useEffect, useState } from 'react';
import { Grid, Paper, Slide, Tab, Tabs } from '@esgian/esgianui';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  getVoyageProfileFilters,
  VOYAGE_PROFILE_PARAMS
} from '@store/features/filters/VoyageAnalyticsPage/VoyageProfile/VoyageProfileSlice';
import { getVoyageEmissionDetails } from '@api';

import { toast } from 'react-toastify';
import { EmissionAndEtsSection, VoyageSummarySection } from '@components/Sections';
import { VoyageSummaryFilterKeys } from '@components/Sections/VoyageAnalytics/VoyageSummarySection/VoyageSummaryConstants';
import { getVoyagesDetails } from '@api/Voyage';
import CompletedVoyageProfileHeader from './CompletedVoyageProfileHeader';

const CompletedVoyageProfilePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    mapOptions: { fullscreen }
  } = useSelector(getVoyageProfileFilters);
  const params = useParams();
  const section = parseInt(searchParams.get('section')) || 1;

  const [calculationType, setCalculationType] = useState('esgian');

  const navigate = useNavigate();
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [esgianData, setEsgianData] = useState(null);
  const [customData, setCustomData] = useState(null);

  const startDate = searchParams.get(VoyageSummaryFilterKeys.DEPARTURE_DATE);
  const endDate = searchParams.get(VoyageSummaryFilterKeys.ARRIVAL_DATE);

  const handleSectionChange = (_, val) => {
    searchParams.set('section', val);
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  useEffect(() => {
    if (searchParams.has('section')) {
      handleSectionChange(null, parseInt(searchParams.get('section')));
    } else {
      searchParams.set('section', section);
      navigate({ search: searchParams.toString() }, { replace: true });
    }
  }, []);

  const [voyageData, setVoyageData] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const getVoyageProfileData = async () => {
      try {
        const filterParams = {
          imo: params.imo,
          fromPortId: parseInt(searchParams.get(VoyageSummaryFilterKeys.FROM_PORT_ID)),
          toPortId: parseInt(searchParams.get(VoyageSummaryFilterKeys.TO_PORT_ID)),
          startDate,
          endDate
        };

        await getVoyagesDetails([filterParams], signal)
          .then((resp) => {
            setVoyageData(resp[0]);
          })
          .catch(() => {
            toast.error('Unable to fetch voyage data.', {
              position: 'top-right'
            });
          })
          .finally(() => {});
      } catch (error) {
        if (error.name === 'AbortError') {
          // console.log('Found abortError');
        } else {
          toast.error('Unable to fetch voyage data.', {
            position: 'top-right'
          });
        }
      }
    };

    let imo = params.imo;
    if (imo && Object.values(VOYAGE_PROFILE_PARAMS).every((param) => searchParams.has(param))) {
      getVoyageProfileData(imo);
    }
    return () => {
      controller.abort();
    };
  }, []);

  const { voyagePortCalls, voyageOverview } = voyageData ?? {};
  const hasEmission = voyageData?.voyageTransitDetails.activeEmission;

  const fetchInitialEmissionAndEtsData = async () => {
    setIsLoadingData(true);
    const controller = new AbortController();
    const { signal } = controller;

    const fromPortId = +searchParams.get(VoyageSummaryFilterKeys.FROM_PORT_ID);
    const toPortId = +searchParams.get(VoyageSummaryFilterKeys.TO_PORT_ID);
    const departureDate = searchParams.get(VoyageSummaryFilterKeys.DEPARTURE_DATE);
    const arrivalDate = searchParams.get(VoyageSummaryFilterKeys.ARRIVAL_DATE);

    const filterParams = {
      imo: params.imo,
      fromPortId: fromPortId,
      toPortId: toPortId,
      startDate: departureDate,
      endDate: arrivalDate,
      myCargoWeight: null,
      myEtsCost: null
    };

    try {
      const [response] = await getVoyageEmissionDetails(filterParams, signal);
      setEsgianData(response);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingData(false);
    }
  };

  const fetchEmissionAndEtsData = async (customizedEtsData) => {
    setIsLoadingData(true);
    const controller = new AbortController();
    const { signal } = controller;

    const fromPortId = +searchParams.get(VoyageSummaryFilterKeys.FROM_PORT_ID);
    const toPortId = +searchParams.get(VoyageSummaryFilterKeys.TO_PORT_ID);
    const departureDate = searchParams.get(VoyageSummaryFilterKeys.DEPARTURE_DATE);
    const arrivalDate = searchParams.get(VoyageSummaryFilterKeys.ARRIVAL_DATE);

    const filterParams = {
      imo: params.imo,
      fromPortId,
      toPortId,
      startDate: departureDate,
      endDate: arrivalDate,
      myCargoWeight: customizedEtsData?.myCargoWeight
        ? parseFloat(customizedEtsData?.myCargoWeight)
        : null,
      myEtsCost: customizedEtsData?.etsCostValue
        ? parseFloat(customizedEtsData?.etsCostValue)
        : null,
      ...customizedEtsData
    };

    try {
      const [response] = await getVoyageEmissionDetails(filterParams, signal);
      if (customizedEtsData) {
        setCustomData(response);
      } else {
        setEsgianData(response);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    // Fetch initial data on mount
    fetchInitialEmissionAndEtsData();
  }, []);

  return (
    <Grid container spacing={fullscreen ? 0 : 2} justifyContent={'stretch'}>
      {!fullscreen && (
        <CompletedVoyageProfileHeader
          voyageData={voyageData}
          voyageOverview={voyageOverview}
          section={section}
          loadedData={calculationType === 'esgian' ? esgianData : customData}
        />
      )}
      {!fullscreen && (
        <Grid item xs={12}>
          <Paper>
            <Tabs onChange={handleSectionChange} value={section || 1}>
              <Tab
                value={1}
                id="voyage-summary-tab"
                label={'Voyage Summary'}
                sx={{ textTransform: 'none' }}
              />
              {hasEmission && esgianData && (
                <Tab
                  value={2}
                  id="emission-and-ets-tab"
                  label={'Emission and ETS'}
                  sx={{ textTransform: 'none' }}
                />
              )}
            </Tabs>
          </Paper>
        </Grid>
      )}
      <Grid item xs={12}>
        <Slide direction="right" in={section === 1} mountOnEnter unmountOnExit>
          <div>
            <VoyageSummarySection
              voyageData={voyageData}
              onUpdateDepartureAndArrivalPort={({ selectedArrivalPort, selectedDeparturePort }) => {
                setSearchParams((prev) => {
                  prev.set(VoyageSummaryFilterKeys.FROM_PORT_ID, selectedDeparturePort?.portId);
                  prev.set(VoyageSummaryFilterKeys.TO_PORT_ID, selectedArrivalPort?.portId);
                  prev.set(
                    VoyageSummaryFilterKeys.DEPARTURE_DATE,
                    selectedDeparturePort?.departureDate
                  );
                  prev.set(VoyageSummaryFilterKeys.ARRIVAL_DATE, selectedArrivalPort?.arrivalDate);
                  return prev;
                });
                window.location.reload();
              }}
            />
          </div>
        </Slide>
        {esgianData && (
          <Slide direction="right" in={section === 2} mountOnEnter unmountOnExit>
            <div>
              <EmissionAndEtsSection
                esgianData={esgianData}
                customData={customData}
                isLoadingData={isLoadingData}
                fetchEmissionAndEtsData={fetchEmissionAndEtsData}
                voyagePortCalls={voyagePortCalls?.portCalls ?? []}
                calculationType={calculationType}
                setCalculationType={setCalculationType}
              />
            </div>
          </Slide>
        )}
      </Grid>
    </Grid>
  );
};

export default CompletedVoyageProfilePage;
